import ItemInfos from "@/src/interfaces/catalog/item-infos"
import '@/src/extensions/string'

export type AnswerType = 'checkbox' | 'radio' | 'add'

type AnswerItemType = 'product' | 'complement' | 'observation' | undefined

export interface AnswerFromCustomer extends ItemInfos.ItemGroup {
    type: AnswerItemType
}

export enum ProductTypeId {
    Product = 1,
    Question = 2,
    Complement = 3,
    Combo = 4,
    ProductBySize = 5,
}

interface AddAnswerProps {
    answersFromCustomer: AnswerFromCustomer[]
    answerSelected: ItemInfos.ItemOption
    group: ItemInfos.ItemGroup
    index: number
    quantity?: number
    isCombo?: boolean
    answerType: AnswerType
}

export default function useSelectWizard() {
    const defineTypeOfQuestion = (group: ItemInfos.ItemOption[]): AnswerItemType => {
        if (group.some(c => c.answerForCustomer)) return 'observation'
        if (group.some(c => [ProductTypeId.Product, ProductTypeId.ProductBySize].includes(c.itemChild?.itemTypeId))) return 'product'
        if (group.some(c => c.itemChild?.itemTypeId === ProductTypeId.Complement)) return 'complement'
        return undefined
    }

    const returnChooseRange = (group: ItemInfos.ItemGroup): Partial<ItemInfos.ItemGroup> => ({
        minQtyItems: group.minQtyItems,
        maxQtyItems: group.maxQtyItems,
        minQtyAnswer: group.minQtyAnswer,
        maxQtyAnswer: group.maxQtyAnswer
    })

    const getAnswersFromCustomer = (props: AddAnswerProps): AnswerFromCustomer[] => {
        const answersCopy = [...props.answersFromCustomer]
        const currentAnswerIndex = answersCopy.findIndex(c => c.id === props.group.id)
        const ranges = returnChooseRange(props.group)

        if (currentAnswerIndex !== -1) {
            const currentAnswer = answersCopy[currentAnswerIndex]
            currentAnswer.itemOptionsGroup = currentAnswer.itemOptionsGroup || []

            const optionIndex = currentAnswer.itemOptionsGroup.findIndex(option => option.id === props.answerSelected.id)

            if (props.answerType !== 'radio') {
                handleQuantitySelection(props, currentAnswer, optionIndex)
            } else {
                handleToggleSelection(props, currentAnswer, optionIndex, ranges)
            }

            answersCopy[currentAnswerIndex] = currentAnswer
        }
        else if (props.quantity !== 0) {
            const answerType = defineTypeOfQuestion(props.group.itemOptionsGroup)
            const newItemGroup: ItemInfos.ItemOption[] = [props.answerSelected]
            props.answerSelected.quantity = props.quantity ?? 1

            const newAnswer: AnswerFromCustomer = {
                ...props.group,
                itemOptionsGroup: newItemGroup,
                type: answerType,
                quantity: props.quantity ?? 1,
                sortIndex: props.index
            }

            answersCopy.push(newAnswer)
        }

        return answersCopy
    }

    const handleQuantitySelection = (props: AddAnswerProps, currentAnswer: AnswerFromCustomer, optionIndex: number) => {
        if (props.quantity === 0 && optionIndex !== -1) {
            currentAnswer.itemOptionsGroup.splice(optionIndex, 1)
        }
        else {
            props.answerSelected.quantity = props.quantity ?? 1

            if (optionIndex !== -1)
                currentAnswer.itemOptionsGroup[optionIndex].quantity = props.quantity ?? 1
            else
                currentAnswer.itemOptionsGroup.push(props.answerSelected)
        }
    }

    const handleToggleSelection = (props: AddAnswerProps, currentAnswer: AnswerFromCustomer, optionIndex: number, ranges: Partial<ItemInfos.ItemGroup>) => {
        if (ranges.maxQtyAnswer === 1 || props.isCombo) {
            props.answerSelected.quantity = props.quantity ?? 1
            currentAnswer.itemOptionsGroup = [props.answerSelected]
        }
        else if (optionIndex === -1 || ranges.maxQtyAnswer && (currentAnswer.itemOptionsGroup.length < ranges.maxQtyAnswer)) {
            if (optionIndex !== -1)
                currentAnswer.itemOptionsGroup.splice(optionIndex, 1)
            else
                currentAnswer.itemOptionsGroup.push(props.answerSelected)
        }
    }

    return { getAnswersFromCustomer }
}